<script lang="ts">
  import type { Snippet } from 'svelte';

  interface Props {
    open: boolean;
    title: string;
    onDismiss: () => void;
    icon?: Snippet;
    children?: Snippet;
    actions: Snippet;
    align?: 'center' | 'start';
    inlineActions?: boolean;
  }

  let { open, title, onDismiss, icon, children, actions, align = 'center', inlineActions = false }: Props = $props();

  const id = $props.id();
  let dialog: HTMLDivElement | null = $state(null);
  let heading: HTMLHeadingElement | null = $state(null);

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  $effect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    queueMicrotask(() => heading?.focus());

    function onKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onDismiss();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === heading)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeydown);
    return () => {
      document.removeEventListener('keydown', onKeydown);
      opener?.focus();
    };
  });
</script>

{#if open}
  <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
  <div
    class="stat-dialog-backdrop"
    onclick={(event) => {
      if (event.target === event.currentTarget) onDismiss();
    }}
  >
    <div
      bind:this={dialog}
      class="stat-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="{id}-title"
      aria-describedby={children ? `${id}-body` : undefined}
      style={align === 'start' ? 'text-align: left' : undefined}
    >
      {#if icon}
        <div class="stat-empty__icon" aria-hidden="true" style="align-self: center; margin-bottom: 0">
          {@render icon()}
        </div>
      {/if}
      <h2 bind:this={heading} id="{id}-title" class="stat-dialog__title" tabindex="-1">{title}</h2>
      {#if children}
        <div id="{id}-body" class="stat-stack stat-stack--sm stat-lead">{@render children()}</div>
      {/if}
      <div class="stat-dialog__actions" class:stat-dialog__actions--inline={inlineActions}>{@render actions()}</div>
    </div>
  </div>
{/if}
